import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AttributeResource } from '@df/catalog/attribute/attribute.resource';
import { AttributeLabelPipe } from '@df/ui/common/attribute-label.pipe';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { Subscription } from 'rxjs/internal/Subscription';
import { SanitizeHtmlService } from '../../../../../../core/util/sanitize-html.service';
import { ProductLayoutComponent } from '../../product-layout.component';
import { NProductLayout } from '../../product-layout.interface';
import { ProductLayoutDetailsSectionsItemComponent } from './item/product-layout-details-sections-item.component';

interface IAttribute {
    attributes: (
        | {
              attribute: NProductLayout.IAttribute;
              mode: NProductLayout.EAttributeMode;
              code: string;
              values: (string | number)[];
              valuesLabels: string;
          }
        | undefined
    )[];
    bulletPoints?: boolean;
    heading: string;
}

@Component({
    selector: 'product-layout-details-sections',
    templateUrl: './product-layout-details-sections.component.html',
    imports: [AttributeLabelPipe, CommonModule, ProductLayoutDetailsSectionsItemComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductLayoutDetailsSectionsComponent extends UiComponent implements OnInit, OnChanges, OnDestroy {
    readonly content = input.required<NProductLayout.IContent>();
    readonly productViewLayout = input.required<ProductLayoutComponent>();

    readonly attributeMode = NProductLayout.EAttributeMode;
    sections: IAttribute[] = [];
    private parentViewDetectChanges?: Subscription;
    private parentViewProductChanges?: Subscription;

    private readonly attributeResource = inject(AttributeResource);
    private readonly sanitizeHtmlService = inject(SanitizeHtmlService);

    override ngOnInit() {
        this.updateData();

        super.ngOnInit();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['productViewLayout']) {
            this.parentViewDetectChanges?.unsubscribe();

            this.parentViewProductChanges?.unsubscribe();

            this.parentViewProductChanges = this.productViewLayout().productChange?.subscribe(() => this.updateData());
        }

        super.ngOnChanges(changes);
    }

    override ngOnDestroy() {
        this.parentViewDetectChanges?.unsubscribe();
        this.parentViewProductChanges?.unsubscribe();

        super.ngOnDestroy();
    }

    private updateData() {
        this.sections = this._getSections();

        this.changeDetectorRef.detectChanges();
    }

    private _getSections(): IAttribute[] {
        const sections = this.content()
            ?.detailsAttributes?.map(item => ({
                heading: item.heading,
                bulletPoints: item.bulletPoints,
                attributes: item.attributes.map(attribute => {
                    const code = attribute.type === 'attribute' ? attribute.code : attribute.codeManual;

                    if (!code) {
                        return undefined;
                    }

                    const values = this.getValues(code);

                    return {
                        attribute,
                        code,
                        mode: attribute.mode,
                        values: this.client.isCms ? values : values.filter(i => !!i),
                        valuesLabels: attribute.mode === NProductLayout.EAttributeMode.cms ? '' : this.getValuesLabels(values, code)
                    };
                })
            }))
            .filter(i => !!i?.attributes?.find(attribute => attribute && !!attribute.values.find(value => !!value)));

        return sections.filter(i => !!i);
    }

    private getValuesLabels(values: (string | number)[], attributeCode: string): string {
        const attribute = this.attributeResource.getExisting(attributeCode);
        const valuesLabels = values.map(value => {
            const labelValue = optionLabelPipe(value, attributeCode);

            return this.sanitizeHtmlService.html(`${labelValue}`);
        });

        if (!attribute?.data?.options) {
            return valuesLabels.join(',');
        } else if (valuesLabels.length) {
            return `${valuesLabels.map(i => `<div class="list-item">${i}</div>`).join('')}`;
        } else {
            return '';
        }
    }

    /**
     * Gets value
     */
    private getValues(code: string): (string | number)[] {
        const value = this.productViewLayout().product()?.data?.[code] || this.productViewLayout().parent()?.data?.[code];

        if (value) {
            return Array.isArray(value) ? value : [value];
        }

        return [];
    }
}
